import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import { useEffect } from 'react';
import {
    Bold,
    Italic,
    Underline as UnderlineIcon,
    Strikethrough,
    List,
    ListOrdered,
    AlignLeft,
    AlignCenter,
    AlignRight,
    Heading1,
    Heading2,
    Heading3,
    Undo,
    Redo,
    Minus,
} from 'lucide-react';
import { Button } from './common/Button';
import { cn } from '@ui/lib/utils';

interface RichTextEditorProps {
    content: string;
    onChange: (content: string) => void;
    placeholder?: string;
    className?: string;
    minHeight?: string;
}

export function RichTextEditor({
    content,
    onChange,
    placeholder = '내용을 입력하세요...',
    className,
    minHeight = '200px',
}: RichTextEditorProps) {
    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: {
                    levels: [1, 2, 3],
                },
            }),
            Underline,
            TextAlign.configure({
                types: ['heading', 'paragraph'],
            }),
            TextStyle,
            Color,
        ],
        content,
        onUpdate: ({ editor }) => {
            onChange(editor.getHTML());
        },
        editorProps: {
            attributes: {
                class: 'prose prose-sm max-w-none focus:outline-none min-h-[inherit] p-3',
            },
        },
    });

    useEffect(() => {
        if (editor && content !== editor.getHTML()) {
            editor.commands.setContent(content);
        }
    }, [content, editor]);

    if (!editor) {
        return null;
    }

    const ToolbarButton = ({
        onClick,
        isActive,
        children,
        title,
    }: {
        onClick: () => void;
        isActive?: boolean;
        children: React.ReactNode;
        title: string;
    }) => (
        <Button
            type="button"
            variant="ghost"
            size="icon"
            className={cn(
                'h-8 w-8',
                isActive && 'bg-neutral-200 text-neutral-900'
            )}
            onClick={onClick}
            title={title}
        >
            {children}
        </Button>
    );

    return (
        <div className={cn('rounded-md border bg-background', className)}>
            {/* Toolbar */}
            <div className="flex flex-wrap items-center gap-0.5 border-b p-1">
                {/* Undo/Redo */}
                <ToolbarButton
                    onClick={() => editor.chain().focus().undo().run()}
                    title="실행 취소"
                >
                    <Undo className="h-4 w-4" />
                </ToolbarButton>
                <ToolbarButton
                    onClick={() => editor.chain().focus().redo().run()}
                    title="다시 실행"
                >
                    <Redo className="h-4 w-4" />
                </ToolbarButton>

                <div className="mx-1 h-6 w-px bg-neutral-200" />

                {/* Headings */}
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                    isActive={editor.isActive('heading', { level: 1 })}
                    title="제목 1"
                >
                    <Heading1 className="h-4 w-4" />
                </ToolbarButton>
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                    isActive={editor.isActive('heading', { level: 2 })}
                    title="제목 2"
                >
                    <Heading2 className="h-4 w-4" />
                </ToolbarButton>
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                    isActive={editor.isActive('heading', { level: 3 })}
                    title="제목 3"
                >
                    <Heading3 className="h-4 w-4" />
                </ToolbarButton>

                <div className="mx-1 h-6 w-px bg-neutral-200" />

                {/* Text formatting */}
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    isActive={editor.isActive('bold')}
                    title="굵게"
                >
                    <Bold className="h-4 w-4" />
                </ToolbarButton>
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    isActive={editor.isActive('italic')}
                    title="기울임"
                >
                    <Italic className="h-4 w-4" />
                </ToolbarButton>
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleUnderline().run()}
                    isActive={editor.isActive('underline')}
                    title="밑줄"
                >
                    <UnderlineIcon className="h-4 w-4" />
                </ToolbarButton>
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleStrike().run()}
                    isActive={editor.isActive('strike')}
                    title="취소선"
                >
                    <Strikethrough className="h-4 w-4" />
                </ToolbarButton>

                <div className="mx-1 h-6 w-px bg-neutral-200" />

                {/* Lists */}
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                    isActive={editor.isActive('bulletList')}
                    title="글머리 기호 목록"
                >
                    <List className="h-4 w-4" />
                </ToolbarButton>
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    isActive={editor.isActive('orderedList')}
                    title="번호 매기기 목록"
                >
                    <ListOrdered className="h-4 w-4" />
                </ToolbarButton>

                <div className="mx-1 h-6 w-px bg-neutral-200" />

                {/* Alignment */}
                <ToolbarButton
                    onClick={() => editor.chain().focus().setTextAlign('left').run()}
                    isActive={editor.isActive({ textAlign: 'left' })}
                    title="왼쪽 정렬"
                >
                    <AlignLeft className="h-4 w-4" />
                </ToolbarButton>
                <ToolbarButton
                    onClick={() => editor.chain().focus().setTextAlign('center').run()}
                    isActive={editor.isActive({ textAlign: 'center' })}
                    title="가운데 정렬"
                >
                    <AlignCenter className="h-4 w-4" />
                </ToolbarButton>
                <ToolbarButton
                    onClick={() => editor.chain().focus().setTextAlign('right').run()}
                    isActive={editor.isActive({ textAlign: 'right' })}
                    title="오른쪽 정렬"
                >
                    <AlignRight className="h-4 w-4" />
                </ToolbarButton>

                <div className="mx-1 h-6 w-px bg-neutral-200" />

                {/* Horizontal rule */}
                <ToolbarButton
                    onClick={() => editor.chain().focus().setHorizontalRule().run()}
                    title="구분선"
                >
                    <Minus className="h-4 w-4" />
                </ToolbarButton>
            </div>

            {/* Editor */}
            <div
                className="overflow-y-auto"
                style={{ minHeight }}
            >
                <EditorContent
                    editor={editor}
                    className="min-h-[inherit]"
                />
            </div>

            {/* Placeholder and prose styling for editor */}
            <style>{`
                .ProseMirror p.is-editor-empty:first-child::before {
                    content: '${placeholder}';
                    color: #9ca3af;
                    float: left;
                    height: 0;
                    pointer-events: none;
                }
                /* Prose-like styling for editor content */
                .ProseMirror {
                    outline: none;
                }
                .ProseMirror h1 {
                    font-size: 1.5rem;
                    font-weight: 700;
                    margin-bottom: 0.75rem;
                    margin-top: 1rem;
                    line-height: 1.3;
                }
                .ProseMirror h2 {
                    font-size: 1.25rem;
                    font-weight: 700;
                    margin-bottom: 0.5rem;
                    margin-top: 0.75rem;
                    line-height: 1.3;
                }
                .ProseMirror h3 {
                    font-size: 1.125rem;
                    font-weight: 600;
                    margin-bottom: 0.5rem;
                    margin-top: 0.75rem;
                    line-height: 1.3;
                }
                .ProseMirror p {
                    margin-bottom: 0.5rem;
                    line-height: 1.6;
                }
                .ProseMirror ul {
                    list-style-type: disc;
                    padding-left: 1.5rem;
                    margin-bottom: 0.5rem;
                }
                .ProseMirror ol {
                    list-style-type: decimal;
                    padding-left: 1.5rem;
                    margin-bottom: 0.5rem;
                }
                .ProseMirror li {
                    margin-bottom: 0.25rem;
                }
                .ProseMirror strong {
                    font-weight: 700;
                }
                .ProseMirror em {
                    font-style: italic;
                }
                .ProseMirror u {
                    text-decoration: underline;
                }
                .ProseMirror s {
                    text-decoration: line-through;
                }
                .ProseMirror hr {
                    border: none;
                    border-top: 1px solid #d1d5db;
                    margin: 1rem 0;
                }
                .ProseMirror [style*="text-align: center"] {
                    text-align: center;
                }
                .ProseMirror [style*="text-align: right"] {
                    text-align: right;
                }
                .ProseMirror [style*="text-align: left"] {
                    text-align: left;
                }
            `}</style>
        </div>
    );
}
